import useSWR from "swr";
import axios, { AxiosError } from "axios";

import { Option } from "models/Option";
import { EquipmentModeCategoryDto } from "models/dto/EquipmentModeCategoryDto";
import { EquipmentModeDto } from "models/dto/EquipmentModeDto";
import { AttributeGroupDto } from "models/dto/AttributeGroupDto";
import { getUniqueOptions } from "utils/getUniqueOptions";

export type CarriersOptions = {
  callActivityOptions: Option<string>[];
  equipmentOptions: Option<string>[];
  carrierCountryOptions: Option<string>[];
  historicalShipmentCitySearchMinLength: number | null;
};

export type GetCarriersOptions = {
  equipmentTypes: EquipmentModeDto[];
  trailerTypes: EquipmentModeCategoryDto[];
  attributes: AttributeGroupDto[];
  usStates: string[];
  caProvinces: string[];
  mxStates: string[];
  callActivityOptions: ("Called Today" | "Not Called Today" | "Both")[];
  carrierCountries: string[];
  historicalShipmentCitySearchMinLength: number;
};

export const useGetCarriersOptions = () => {
  const { data, error, isLoading } = useSWR<GetCarriersOptions, AxiosError>(
    "/api/carriers/options",
    (url) =>
      axios.get<GetCarriersOptions>(url).then((response) => response.data),
    { keepPreviousData: true, revalidateOnFocus: false }
  );

  return {
    data: data
      ? mapCarriersOptionsToOptions(data)
      : getDefaultCarriersOptions(),
    error,
    isLoading,
  };
};

const getDefaultCarriersOptions = (): CarriersOptions => ({
  callActivityOptions: [],
  equipmentOptions: [],
  carrierCountryOptions: [],
  historicalShipmentCitySearchMinLength: null,
});

export const mapCarriersOptionsToOptions = (
  data: GetCarriersOptions
): CarriersOptions => ({
  equipmentOptions: mapEquipmentToOptions(data.equipmentTypes),
  callActivityOptions: mapCallActivityToOptions(data.callActivityOptions),
  carrierCountryOptions: mapCountriesToOptions(data.carrierCountries),
  historicalShipmentCitySearchMinLength:
    data.historicalShipmentCitySearchMinLength,
});

const mapEquipmentToOptions = <
  T extends EquipmentModeDto | EquipmentModeCategoryDto,
>(
  data: T[]
): Option<T["id"]>[] =>
  data.map((equipment) => ({
    key: equipment.id,
    value: equipment.id,
    text: equipment.text,
  }));

const mapCountriesToOptions = (data: string[]): Option<string>[] =>
  getUniqueOptions(
    data.map((country) => ({
      key: country,
      value: country,
      text: country,
    }))
  );

const mapCallActivityToOptions = (data: string[]): Option<string>[] =>
  data.map((callActivity) => ({
    key: callActivity,
    value: callActivity,
    text: callActivity,
  }));
